import { useNavigate } from 'react-router-dom';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function EnlistedDashboard({ user, data }) {
  const navigate = useNavigate();
  const profile = data?.profile || {};
  const training = data?.training_status || [];
  const shifts = data?.deployment_schedule || [];

  return (
    <div className="space-y-6" data-testid="enlisted-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">My Dashboard</h1>
        <p className="text-gray-400">Welcome, {user.full_name}</p>
      </div>

      {/* Personal Profile */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Personal Profile</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-gray-400 text-sm">Service Number</p>
            <p className="text-white font-medium">{profile.service_number || '-'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Rank</p>
            <p className="text-amber-400 font-medium">{profile.rank || '-'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Status</p>
            <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">{profile.status || '-'}</span>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Department</p>
            <p className="text-white">{profile.department || '-'}</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-700">
          <ClearanceIndicator label="Medical" cleared={profile.medical_clearance} />
          <ClearanceIndicator label="Weapons" cleared={profile.weapons_clearance} />
          <ClearanceIndicator label="Deployable" cleared={profile.deployment_eligible} />
          <div>
            <p className="text-gray-400 text-sm">Performance</p>
            <p className="text-white font-bold">{profile.performance_score ? `${profile.performance_score}/100` : '-'}</p>
          </div>
        </div>
      </div>

      {/* Training Status */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Training Status</h2>
        {training.length > 0 ? (
          <div className="space-y-3">
            {training.map(t => (
              <TrainingCard key={t.enrollment_id} training={t} />
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No active training enrollments</p>
        )}
        <button onClick={() => navigate('/training')} className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm">
          View All Training
        </button>
      </div>

      {/* Deployment Schedule */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Upcoming Shifts</h2>
        {shifts.length > 0 ? (
          <div className="space-y-3">
            {shifts.map(s => (
              <ShiftCard key={s.shift_id} shift={s} />
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No upcoming shifts scheduled</p>
        )}
        <button onClick={() => navigate('/deployments')} className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white text-sm">
          View Deployments
        </button>
      </div>
    </div>
  );
}

function ClearanceIndicator({ label, cleared }) {
  return (
    <div className="flex items-center">
      <span className={`w-3 h-3 rounded-full mr-2 ${cleared ? 'bg-green-500' : 'bg-red-500'}`}></span>
      <span className="text-gray-300 text-sm">{label}</span>
    </div>
  );
}

function TrainingCard({ training }) {
  const progress = training.total_modules > 0 ? (training.passed_evaluations / training.total_modules) * 100 : 0;
  return (
    <div className="bg-gray-700 rounded-lg p-4">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white font-medium">{training.program_name}</p>
          <p className="text-gray-400 text-sm">{training.program_code}</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs ${
          training.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
        }`}>{training.status}</span>
      </div>
      <div className="mt-3">
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>Progress</span>
          <span>{training.passed_evaluations}/{training.total_modules} modules</span>
        </div>
        <div className="w-full bg-gray-600 rounded-full h-2">
          <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
        </div>
      </div>
    </div>
  );
}

function ShiftCard({ shift }) {
  return (
    <div className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
      <div>
        <p className="text-white font-medium">{shift.site_name}</p>
        <p className="text-gray-400 text-sm">{shift.team_name}</p>
        {shift.site_address && <p className="text-gray-500 text-xs">{shift.site_address}</p>}
      </div>
      <div className="text-right">
        <p className="text-amber-400 font-medium">{formatDate(shift.date)}</p>
        <p className="text-gray-400 text-sm">{shift.start_time || '00:00'} - {shift.end_time || '00:00'}</p>
      </div>
    </div>
  );
}

export default EnlistedDashboard;
